import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  PageToolbar,
  SectionTitle,
  SectionSubtitle,
  ToolbarDivider,
} from '../../components/ui/PageLayout';
import {
  AnimatedTabIndicator,
  animatedTabTriggerClass,
} from '../../components/ui/AnimatedTabIndicator';
import ConnectRepositoryToProject from '../../components/ConnectRepositoryToProject/ConnectRepositoryToProject';
import { useConfirm } from '../../contexts/ConfirmContext';
import { FeatureGate, useFeatureFlag } from '../../contexts/FeatureFlagContext';
import {
  ArrowLeft,
  GitFork,
  Plus,
  Trash2,
  Users,
  BarChart3,
  Briefcase,
  FileText,
  Sparkles,
} from 'lucide-react';
import ProjectDoraMetricsGrid from './ProjectDoraMetricsGrid';
import CycleTimeSection from '../../components/dora/CycleTimeSection';
import { CycleTimeSummaryMetricCards } from '../../components/dora/CycleTimeSummaryMetricCard';
import CodeCompositionOverTimeCard from '../../components/dora/CodeCompositionOverTimeCard';
import ProjectSettingsModal from './ProjectSettingsModal';
import AssignProjectMemberModal from './AssignProjectMemberModal';
import OrgMemberAvatar from '../../components/ui/OrgMemberAvatar';
import {
  teamSectionHeaderClass,
  teamMemberRowClass,
  memberNameClass,
  memberRoleClass,
} from '../../components/members/memberViewTheme';
import ProjectActivityHeatmapSection from './ProjectActivityHeatmapSection';
import ProjectMembersContributorsBarChartSection from './ProjectMembersContributorsBarChartSection';
import { useProjectDetailOrgAndProject } from './useProjectDetailOrgAndProject';
import { useProjectDetailDoraAndContributors } from './useProjectDetailDoraAndContributors';
import { useProjectDetailQueries } from './useProjectDetailQueries';
import { Skeleton, CardGridSkeleton } from '../../components/ui/Skeleton';
import { useOrganisationName } from '../../hooks/useOrganisationName';
import ProjectPortfolioWikiTab from './ProjectPortfolioWikiTab';
import ProjectDocumentsPanel from './ProjectDocumentsPanel';
import ProjectAskPanel from './ProjectAskPanel';
import RepositoriesConnectedTable from '../../components/repositories/RepositoriesConnectedTable';

const ProjectDetail = () => {
  const { streamId, projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { confirm } = useConfirm();
  const orgName = useOrganisationName();
  const { orgId, project, loading, refetchProject } = useProjectDetailOrgAndProject(
    streamId,
    projectId
  );
  const {
    repositories,
    dashboardStats,
    projectMembers,
    reposLoading,
    refetchRepos,
    refetchDashboard,
    refetchMembers,
    removeMemberMutation,
  } = useProjectDetailQueries(orgId, streamId, projectId);
  const [addRepoOpen, setAddRepoOpen] = useState(false);
  const [settingsProjectOpen, setSettingsProjectOpen] = useState(false);
  const [assignMemberOpen, setAssignMemberOpen] = useState(false);
  const [deploymentPeriod, setDeploymentPeriod] = useState('yearly');
  const [leadTimePeriod, setLeadTimePeriod] = useState('yearly');
  const [doraChartType, setDoraChartType] = useState('area');
  const [leadTimeChartType, setLeadTimeChartType] = useState('area');
  const [churnPeriod, setChurnPeriod] = useState('yearly');
  const [churnChartType, setChurnChartType] = useState('area');
  const [contributorsTimeframe, setContributorsTimeframe] = useState('week');
  const [contributorsStatus, setContributorsStatus] = useState('active');
  const [activeProjectTab, setActiveProjectTab] = useState('overview');
  const showProjectReposTab = useFeatureFlag('ui.projectDetail.repositoriesSection', false);
  const showProjectMembersTab = useFeatureFlag('ui.projectDetail.membersSection', false);
  const showPortfolioWikiTab = useFeatureFlag('ui.projectDetail.portfolioWiki', false);
  const showDocumentsTab = useFeatureFlag('ui.projectDetail.documentsTab', false);
  const showAskPanel = useFeatureFlag('ui.projectDetail.askPanel', false);
  const knowSourcesRead = useFeatureFlag('api.projects.knowledgeSources.read', false);
  const knowSourcesWrite = useFeatureFlag('api.projects.knowledgeSources.write', false);
  const deploymentWindowDays =
    deploymentPeriod === 'weekly' ? 7 : deploymentPeriod === 'yearly' ? 365 : 30;
  const leadTimeWindowDays =
    leadTimePeriod === 'weekly' ? 7 : leadTimePeriod === 'yearly' ? 365 : 30;
  const churnWindowDays = churnPeriod === 'weekly' ? 7 : churnPeriod === 'yearly' ? 365 : 30;

  const {
    deploymentDora,
    deploymentDoraMeta,
    deploymentLoading,
    leadTimeDora,
    leadTimeDoraMeta,
    leadTimeLoading,
    churnData,
    churnLoading,
    projectContributorRows,
    projectContributorsLoading,
  } = useProjectDetailDoraAndContributors(orgId, streamId, projectId, {
    deploymentWindowDays,
    leadTimeWindowDays,
    churnWindowDays,
    contributorsTimeframe,
    contributorsStatus,
  });

  useEffect(() => {
    setActiveProjectTab('overview');
  }, [streamId, projectId]);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'portfolio' && showPortfolioWikiTab) {
      setActiveProjectTab('portfolio');
    } else if (tabParam === 'documents' && showDocumentsTab) {
      setActiveProjectTab('documents');
    } else if (tabParam === 'ask' && showAskPanel) {
      setActiveProjectTab('ask');
    }
  }, [streamId, projectId, searchParams, showPortfolioWikiTab, showDocumentsTab, showAskPanel]);

  useEffect(() => {
    if (!showProjectReposTab && activeProjectTab === 'repositories') {
      setActiveProjectTab('overview');
    }
  }, [showProjectReposTab, activeProjectTab]);

  useEffect(() => {
    if (!showProjectMembersTab && activeProjectTab === 'members') {
      setActiveProjectTab('overview');
    }
  }, [showProjectMembersTab, activeProjectTab]);

  useEffect(() => {
    if (!showPortfolioWikiTab && activeProjectTab === 'portfolio') {
      setActiveProjectTab('overview');
    }
  }, [showPortfolioWikiTab, activeProjectTab]);

  useEffect(() => {
    if (!showDocumentsTab && activeProjectTab === 'documents') {
      setActiveProjectTab('overview');
    }
  }, [showDocumentsTab, activeProjectTab]);

  useEffect(() => {
    if (!showAskPanel && activeProjectTab === 'ask') {
      setActiveProjectTab('overview');
    }
  }, [showAskPanel, activeProjectTab]);

  const projectDetailTabs = useMemo(() => {
    const tabs = [{ id: 'overview', label: 'Overview', icon: BarChart3 }];
    tabs.push({ id: 'metrics', label: 'Metrics', icon: BarChart3 });
    if (showProjectReposTab) {
      tabs.push({ id: 'repositories', label: 'Repositories', icon: GitFork });
    }
    if (showProjectMembersTab) {
      tabs.push({ id: 'members', label: 'Members', icon: Users });
    }
    if (showDocumentsTab) {
      tabs.push({ id: 'documents', label: 'Documents', icon: FileText });
    }
    if (showPortfolioWikiTab) {
      tabs.push({ id: 'portfolio', label: 'Portfolio', icon: Briefcase });
    }
    return tabs;
  }, [showProjectReposTab, showProjectMembersTab, showPortfolioWikiTab, showDocumentsTab]);

  const onProjectTabListKeyDown = useCallback(
    (e) => {
      const ids = projectDetailTabs.map((t) => t.id);
      const idx = ids.indexOf(activeProjectTab);
      if (idx < 0) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActiveProjectTab(ids[(idx + 1) % ids.length]);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActiveProjectTab(ids[(idx - 1 + ids.length) % ids.length]);
      } else if (e.key === 'Home') {
        e.preventDefault();
        setActiveProjectTab(ids[0]);
      } else if (e.key === 'End') {
        e.preventDefault();
        setActiveProjectTab(ids[ids.length - 1]);
      }
    },
    [projectDetailTabs, activeProjectTab]
  );

  const handleRemoveProjectMember = async (userId) => {
    if (!orgId || !streamId || !projectId) return;
    const member = projectMembers.find((m) => (m.userId || m.id) === userId);
    const name = member?.username || member?.email || 'this member';
    const ok = await confirm({
      title: 'Remove member from project',
      message: `Remove ${name} from this project?`,
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    try {
      await removeMemberMutation.mutateAsync(userId);
    } catch {
      // toast handled in mutation
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="mb-4 flex items-center gap-2">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-10 w-10 rounded-lg" />
        </div>
        <div className="card-mdl-static p-6 mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Skeleton className="h-10 w-10 rounded-lg flex-shrink-0" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
        </div>
        <Skeleton className="h-6 w-32 mb-3" />
        <CardGridSkeleton count={4} />
      </PageContainer>
    );
  }

  if (!project) {
    return (
      <PageContainer>
        <PageToolbar title="Project" />
        <p className="text-slate-400">Project not found.</p>
        <button
          type="button"
          onClick={() => navigate('/projects')}
          className="mt-4 btn-icon-mdl"
          aria-label="Back to Projects"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </PageContainer>
    );
  }

  const orgLabel = orgName || 'Organisation';
  const streamLabel = project.stream?.name || 'Stream';

  return (
    <PageContainer>
      <PageToolbar
        title={project.name}
        breadcrumbItems={[
          { label: orgLabel, to: '/' },
          ...(streamId ? [{ label: streamLabel, to: `/streams/${streamId}` }] : []),
          { label: project.name },
        ]}
      ></PageToolbar>
      <ToolbarDivider />

      <div className="border-b border-dark-border mb-6 mt-2">
        <nav
          className="flex gap-1 -mb-px overflow-x-auto"
          role="tablist"
          aria-label="Project sections"
          onKeyDown={onProjectTabListKeyDown}
        >
          {projectDetailTabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              id={`project-tab-${id}`}
              aria-selected={activeProjectTab === id}
              aria-controls={`project-tabpanel-${id}`}
              tabIndex={activeProjectTab === id ? 0 : -1}
              onClick={() => {
                setActiveProjectTab(id);
                const next = new URLSearchParams(searchParams);
                if (id === 'portfolio') next.set('tab', 'portfolio');
                else if (id === 'documents') next.set('tab', 'documents');
                else if (id === 'ask') next.set('tab', 'ask');
                else next.delete('tab');
                setSearchParams(next, { replace: true });
              }}
              className={animatedTabTriggerClass(activeProjectTab === id)}
            >
              <AnimatedTabIndicator
                layoutId="project-detail-tab-indicator"
                isActive={activeProjectTab === id}
              />
              <span className="relative z-10 inline-flex items-center gap-2">
                <Icon className="w-4 h-4" aria-hidden />
                {label}
              </span>
            </button>
          ))}
        </nav>
      </div>

      <div>
        {activeProjectTab === 'overview' && (
          <div
            role="tabpanel"
            id="project-tabpanel-overview"
            aria-labelledby="project-tab-overview"
          >
            <FeatureGate flag="ui.projectDetail.dashboardSection">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                <div className="card-mdl-static px-4 py-3">
                  <p className="text-xs text-slate-500 mb-1">Repositories</p>
                  <p className="text-xl font-bold text-slate-100">
                    {dashboardStats?.totalRepositories ?? 0}
                  </p>
                </div>
                <div className="card-mdl-static px-4 py-3">
                  <p className="text-xs text-slate-500 mb-1">Commits</p>
                  <p className="text-xl font-bold text-slate-100">
                    {dashboardStats?.totalCommits ?? 0}
                  </p>
                </div>
                <div className="card-mdl-static px-4 py-3">
                  <p className="text-xs text-slate-500 mb-1">Contributors (30d)</p>
                  <p className="text-xl font-bold text-slate-100">
                    {dashboardStats?.activeContributors ?? 0}
                  </p>
                </div>
                <div className="card-mdl-static px-4 py-3">
                  <p className="text-xs text-slate-500 mb-1">Open PRs</p>
                  <p
                    className={`text-xl font-bold ${(dashboardStats?.openPullRequests ?? 0) > 0 ? 'text-amber-400' : 'text-slate-100'}`}
                  >
                    {dashboardStats?.openPullRequests ?? 0}
                  </p>
                </div>
                <div className="card-mdl-static px-4 py-3">
                  <p className="text-xs text-slate-500 mb-1">Deployments</p>
                  <p className="text-xl font-bold text-slate-100">
                    {dashboardStats?.deploymentsToDeploymentBranch ?? 0}
                  </p>
                </div>
                <div className="card-mdl-static px-4 py-3">
                  <p className="text-xs text-slate-500 mb-1">Mean Lead Time</p>
                  <p className="text-xl font-bold text-slate-100">
                    {dashboardStats?.meanLeadTimeHours != null
                      ? `${dashboardStats.meanLeadTimeHours}h`
                      : '—'}
                  </p>
                </div>
              </div>
            </FeatureGate>

            <FeatureGate flag="ui.projectDetail.cycleTimeSection">
              <div className="section-block-mdl">
                <CycleTimeSection
                  className="!mt-0"
                  fetchScope={
                    orgId && streamId && projectId
                      ? { orgId, streamId, projectId }
                      : undefined
                  }
                />
              </div>
              <div className="section-block-mdl">
                <CycleTimeSummaryMetricCards
                  fetchScope={
                    orgId && streamId && projectId
                      ? { orgId, streamId, projectId }
                      : undefined
                  }
                />
              </div>
            </FeatureGate>

            <ProjectActivityHeatmapSection
              orgId={orgId}
              streamId={streamId}
              projectId={projectId}
            />

            <div className="card-grid-2-mdl gap-4 mt-4">
              {/* Vanti Insights card */}
              <div className="card-mdl-static p-4 flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-brand-purple to-brand-teal">
                    <Sparkles className="h-3 w-3 text-white" aria-hidden />
                  </div>
                  <span className="text-sm font-semibold text-slate-100">Vanti insights</span>
                </div>
                <div className="space-y-2">
                  {(dashboardStats?.openPullRequests ?? 0) > 0 && (
                    <div className="flex items-start gap-2 rounded-lg bg-amber-900/20 border border-amber-700/30 px-3 py-2">
                      <span
                        className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-400 flex-shrink-0"
                        aria-hidden
                      />
                      <p className="text-xs text-amber-300">
                        {dashboardStats.openPullRequests} open pull request
                        {dashboardStats.openPullRequests === 1 ? '' : 's'} awaiting review
                      </p>
                    </div>
                  )}
                  {(dashboardStats?.commitsNotOnDeploymentBranch?.length ?? 0) > 0 && (
                    <button
                      type="button"
                      onClick={() => setActiveProjectTab('metrics')}
                      className="w-full text-left flex items-start gap-2 rounded-lg bg-red-900/20 border border-red-700/30 px-3 py-2 hover:border-red-600/50 transition-colors"
                    >
                      <span
                        className="mt-1 h-1.5 w-1.5 rounded-full bg-red-400 flex-shrink-0"
                        aria-hidden
                      />
                      <p className="text-xs text-red-300">
                        {dashboardStats.commitsNotOnDeploymentBranch.length} commit
                        {dashboardStats.commitsNotOnDeploymentBranch.length === 1 ? '' : 's'} not
                        yet on deployment branch
                      </p>
                    </button>
                  )}
                  {(dashboardStats?.openPullRequests ?? 0) <= 0 &&
                    (dashboardStats?.commitsNotOnDeploymentBranch?.length ?? 0) <= 0 && (
                      <div className="flex items-start gap-2 rounded-lg bg-emerald-900/20 border border-emerald-700/30 px-3 py-2">
                        <span
                          className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-400 flex-shrink-0"
                          aria-hidden
                        />
                        <p className="text-xs text-emerald-300">
                          Everything looks healthy. No issues detected.
                        </p>
                      </div>
                    )}
                </div>
              </div>

              {/* Recent activity card */}
              <div className="card-mdl-static p-4">
                <span className="text-sm font-semibold text-slate-100">Recent activity</span>
                <div className="mt-3 space-y-0.5">
                  {dashboardStats?.recentActivity?.length > 0 ? (
                    dashboardStats.recentActivity.slice(0, 5).map((a, i) => (
                      <div
                        key={a.id}
                        className={`flex items-start gap-2.5 py-2 ${i < Math.min(dashboardStats.recentActivity.length, 5) - 1 ? 'border-b border-dark-border' : ''}`}
                      >
                        <span
                          className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand-purple-light"
                          aria-hidden
                        />
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-slate-300 truncate">{a.type}</p>
                          {(a.repository || a.details) && (
                            <p className="text-xs text-slate-500 truncate mt-0.5">
                              {[a.repository, a.details].filter(Boolean).join(' · ')}
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">No recent activity.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeProjectTab === 'metrics' && (
          <div
            role="tabpanel"
            id="project-tabpanel-metrics"
            aria-labelledby="project-tab-metrics"
            className="dashboard-org-tight"
          >
            <FeatureGate flag="ui.projectDetail.cycleTimeSection">
              <div className="section-block-mdl">
                <CodeCompositionOverTimeCard
                  className="!mt-0"
                  fetchScope={
                    orgId && streamId && projectId
                      ? { orgId, streamId, projectId }
                      : undefined
                  }
                />
              </div>
            </FeatureGate>

            <ProjectDoraMetricsGrid
              deploymentLoading={deploymentLoading}
              deploymentDora={deploymentDora}
              deploymentDoraMeta={deploymentDoraMeta}
              deploymentWindowDays={deploymentWindowDays}
              deploymentPeriod={deploymentPeriod}
              doraChartType={doraChartType}
              setDeploymentPeriod={setDeploymentPeriod}
              setDoraChartType={setDoraChartType}
              leadTimeLoading={leadTimeLoading}
              leadTimeDora={leadTimeDora}
              leadTimeDoraMeta={leadTimeDoraMeta}
              leadTimePeriod={leadTimePeriod}
              leadTimeChartType={leadTimeChartType}
              setLeadTimePeriod={setLeadTimePeriod}
              setLeadTimeChartType={setLeadTimeChartType}
              churnLoading={churnLoading}
              churnData={churnData}
              churnWindowDays={churnWindowDays}
              churnPeriod={churnPeriod}
              churnChartType={churnChartType}
              setChurnPeriod={setChurnPeriod}
              setChurnChartType={setChurnChartType}
              metricsFetchScope={
                orgId && streamId && projectId ? { orgId, streamId, projectId } : undefined
              }
            />

            {dashboardStats?.commitsNotOnDeploymentBranch?.length > 0 && (
              <div className="section-block-mdl">
                <SectionTitle>Commits not on deployment branch</SectionTitle>
                <p className="text-sm text-slate-400 mb-3">
                  Commits that have not yet reached the deployment branch (e.g. on feature
                  branches).
                </p>
                <ul className="card-mdl-static divide-y divide-dark-border max-h-[400px] overflow-y-auto">
                  {dashboardStats.commitsNotOnDeploymentBranch.map((c) => (
                    <li
                      key={c.id}
                      className="px-4 py-3 text-sm flex flex-wrap items-center gap-x-2 gap-y-1"
                    >
                      <span
                        className="font-mono text-xs text-slate-400 truncate max-w-[80px]"
                        title={c.sha}
                      >
                        {c.sha.slice(0, 7)}
                      </span>
                      <span
                        className="font-medium text-slate-100 truncate flex-1 min-w-0"
                        title={c.message}
                      >
                        {c.message.split('\n')[0]}
                      </span>
                      <span className="text-slate-400">{c.authorName}</span>
                      <span className="inline-flex items-center rounded-md bg-amber-900/30 px-2 py-0.5 text-amber-200 font-medium">
                        {c.branch} → {(c.deploymentBranches || [c.primaryDeploymentBranch]).join(', ')}
                      </span>
                      <span className="text-slate-500 text-xs">{c.repositoryName}</span>
                      <span className="text-slate-500 text-xs">
                        {new Date(c.authorDate).toLocaleDateString()}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {activeProjectTab === 'repositories' && showProjectReposTab && (
          <div
            role="tabpanel"
            id="project-tabpanel-repositories"
            aria-labelledby="project-tab-repositories"
          >
            <div className="flex items-center justify-between mb-3">
              <button
                type="button"
                onClick={() => setAddRepoOpen(true)}
                className="btn-icon-mdl ml-auto"
                aria-label="Add repository"
                title="Add repository"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
            <RepositoriesConnectedTable
              repositories={repositories}
              loading={reposLoading}
              navigate={navigate}
              emptyIcon={<GitFork className="w-12 h-12 text-slate-500" />}
              emptyTitle="No repositories connected yet"
              emptyDescription="Connect GitHub repositories to this project so DORA metrics, cycle time, and contributor charts appear on the project dashboard."
              emptyAction={
                <button
                  type="button"
                  onClick={() => setAddRepoOpen(true)}
                  className="mt-4 btn-primary-mdl inline-flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  Connect repositories
                </button>
              }
            />
          </div>
        )}

        {activeProjectTab === 'members' && showProjectMembersTab && (
          <div role="tabpanel" id="project-tabpanel-members" aria-labelledby="project-tab-members">
            <div className="mb-6">
              <div className={`${teamSectionHeaderClass} mb-0 rounded-t-xl`}>
                <SectionSubtitle className="text-slate-200">Project members</SectionSubtitle>
                <button
                  type="button"
                  onClick={() => setAssignMemberOpen(true)}
                  className="btn-icon-mdl"
                  aria-label="Add member"
                  title="Add member to project"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              {projectMembers.length === 0 ? (
                <p className="px-4 py-6 text-slate-400">No members assigned to this project.</p>
              ) : (
                <ul className="card-mdl-static overflow-hidden rounded-b-xl rounded-t-none border-t-0">
                  {projectMembers.map((m) => {
                    const uid = m.userId || m.id;
                    return (
                      <li key={uid} className={teamMemberRowClass}>
                        <span className={`flex min-w-0 items-center gap-2 ${memberNameClass}`}>
                          <OrgMemberAvatar member={m} size="sm" />
                          <span className="min-w-0 truncate">
                            {m.username}
                            {m.email && (
                              <span className={`${memberRoleClass} ml-1`}>({m.email})</span>
                            )}
                          </span>
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveProjectMember(uid)}
                          className="btn-icon-mdl btn-icon-mdl-sm text-slate-400 hover:text-red-600 hover:bg-red-900/20"
                          aria-label={`Remove ${m.username || m.email}`}
                          title="Remove from project"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <ProjectMembersContributorsBarChartSection
              projectId={projectId}
              projectContributorRows={projectContributorRows}
              projectContributorsLoading={projectContributorsLoading}
              contributorsTimeframe={contributorsTimeframe}
              contributorsStatus={contributorsStatus}
              setContributorsTimeframe={setContributorsTimeframe}
              setContributorsStatus={setContributorsStatus}
            />
          </div>
        )}

        {activeProjectTab === 'portfolio' && showPortfolioWikiTab && (
          <div
            role="tabpanel"
            id="project-tabpanel-portfolio"
            aria-labelledby="project-tab-portfolio"
          >
            <ProjectPortfolioWikiTab
              orgId={orgId}
              streamId={streamId}
              projectId={projectId}
              projectName={project.name}
              repositories={repositories}
            />
          </div>
        )}

        {activeProjectTab === 'documents' && showDocumentsTab && (
          <div
            role="tabpanel"
            id="project-tabpanel-documents"
            aria-labelledby="project-tab-documents"
          >
            <ProjectDocumentsPanel
              orgId={orgId}
              streamId={streamId}
              projectId={projectId}
              knowRead={knowSourcesRead}
              knowWrite={knowSourcesWrite}
              title="Project documents"
              description="Upload files or add URLs for this project. Content is chunked and embedded for portfolio briefings and for Vanti when you chat from this project."
            />
          </div>
        )}

        {activeProjectTab === 'ask' && showAskPanel && (
          <div role="tabpanel" id="project-tabpanel-ask" aria-labelledby="project-tab-ask">
            <div className="max-w-3xl">
              <ProjectAskPanel projectId={projectId} streamId={streamId} />
            </div>
          </div>
        )}
      </div>

      <ConnectRepositoryToProject
        isOpen={addRepoOpen}
        onClose={() => setAddRepoOpen(false)}
        orgId={orgId}
        streamId={streamId}
        projectId={projectId}
        onSuccess={() => {
          void refetchRepos();
        }}
      />
      <ProjectSettingsModal
        isOpen={settingsProjectOpen}
        onClose={() => setSettingsProjectOpen(false)}
        orgId={orgId}
        streamId={streamId}
        projectId={projectId}
        project={project}
        onSuccess={() => {
          void refetchProject();
        }}
      />
      <AssignProjectMemberModal
        isOpen={assignMemberOpen}
        onClose={() => setAssignMemberOpen(false)}
        orgId={orgId}
        streamId={streamId}
        projectId={projectId}
        projectName={project?.name}
        onSuccess={() => {
          void refetchMembers();
          void refetchDashboard();
        }}
      />
    </PageContainer>
  );
};

export default ProjectDetail;
